/**
 * Versioned pixel-art animation metadata schema + validator (spec.md §N.3).
 *
 * Animation data is **config, not code**: a `*.anim.json` (or the equivalent TS
 * object) describes the spritesheet grid and per-state frame ranges/fps/events.
 * Entities reference animations by name; nothing here is hardcoded in gameplay.
 *
 * The validator runs in tests/CI and at load time so a malformed metadata object
 * **fails fast** with a readable list of problems rather than blowing up mid-render
 * (spec.md §N.3). It collects *all* errors instead of throwing on the first.
 *
 * Documented rules (referenced by the dev preview tool + asset docs):
 *  - `version` must equal {@link ANIM_SCHEMA_VERSION}.
 *  - `frameWidth`/`frameHeight` are positive integers; `scale` (optional) is a
 *    positive number.
 *  - visual presentation overrides are optional, per-asset render-only fields:
 *    `visualScale`/`shadowScale` must be positive numbers; visual/shadow offsets must be finite
 *    numbers in rendered CSS pixels.
 *  - `defaultState` must be a key present in `states`.
 *  - every state needs a non-empty `frames` array of non-negative integer sheet
 *    indices, a positive `fps`, and a boolean `loop`.
 *  - a frame event's `frame` must be one of that state's `frames` values (it names
 *    the displayed sheet index, not an array position).
 *  - **unknown event `type`s fail validation** (strict). As defense-in-depth the
 *    runtime player also ignores any event type it does not handle.
 *  - `sfx` and `effect` events require a non-empty `name`.
 */

export const ANIM_SCHEMA_VERSION = 1 as const;

export const FRAME_EVENT_TYPES = [
  'hitFrame',
  'sfx',
  'effect',
  'cameraShake',
  'hitStop',
  'spawnFloatingText',
] as const;

export type FrameEventType = (typeof FRAME_EVENT_TYPES)[number];

/** A per-frame trigger. `name` is required for `sfx`/`effect`; other keys are event-specific params. */
export interface FrameEvent {
  /** A sheet frame index that appears in the owning state's `frames` array. */
  frame: number;
  type: FrameEventType;
  /** SFX key (for `sfx`) or effect name (for `effect`). */
  name?: string;
  /** Optional event params (cameraShake intensity/durationMs, hitStop durationMs, floating text, …). */
  intensity?: number;
  durationMs?: number;
  text?: string;
}

export interface AnimationState {
  /** Ordered sheet frame indices to display. */
  frames: number[];
  fps: number;
  loop: boolean;
  /** When a non-looping state completes, hold the final frame instead of resetting. */
  holdLastFrame?: boolean;
  events?: FrameEvent[];
}

export interface AnimationMeta {
  version: number;
  /** Spritesheet filename, resolved relative to the metadata file. */
  image: string;
  frameWidth: number;
  frameHeight: number;
  /** Integer-friendly upscale factor for nearest-neighbor rendering. */
  scale?: number;
  /** Render-only multiplier applied after `scale`; does not affect gameplay footprint. */
  visualScale?: number;
  /** Render-only x offset in CSS pixels; does not affect gameplay footprint. */
  visualOffsetX?: number;
  /** Render-only y offset in CSS pixels; does not affect gameplay footprint. */
  visualOffsetY?: number;
  /** Render-only shadow y offset in CSS pixels; does not affect gameplay footprint. */
  shadowOffsetY?: number;
  /** Render-only shadow width multiplier; does not affect gameplay footprint. */
  shadowScale?: number;
  defaultState: string;
  states: Record<string, AnimationState>;
}

export type ValidationResult =
  | { valid: true; meta: AnimationMeta }
  | { valid: false; errors: string[] };

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isPositiveInt = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v > 0;

const isNonNegativeInt = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0;

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const isPositiveNumber = (v: unknown): v is number =>
  isFiniteNumber(v) && v > 0;

function validateEvent(stateName: string, idx: number, frames: number[], ev: unknown, errors: string[]): void {
  const where = `states.${stateName}.events[${idx}]`;
  if (!isObject(ev)) {
    errors.push(`${where} must be an object`);
    return;
  }
  if (!isNonNegativeInt(ev.frame)) {
    errors.push(`${where}.frame must be a non-negative integer`);
  } else if (!frames.includes(ev.frame)) {
    errors.push(`${where}.frame ${ev.frame} is not one of state "${stateName}" frames [${frames.join(', ')}]`);
  }
  if (typeof ev.type !== 'string' || !(FRAME_EVENT_TYPES as readonly string[]).includes(ev.type)) {
    errors.push(`${where}.type "${String(ev.type)}" is an unknown event type (allowed: ${FRAME_EVENT_TYPES.join(', ')})`);
    return;
  }
  if ((ev.type === 'sfx' || ev.type === 'effect') && (typeof ev.name !== 'string' || ev.name.length === 0)) {
    errors.push(`${where}.name is required (non-empty string) for "${ev.type}" events`);
  }
}

function validateState(name: string, state: unknown, errors: string[]): void {
  const where = `states.${name}`;
  if (!isObject(state)) {
    errors.push(`${where} must be an object`);
    return;
  }
  if (!Array.isArray(state.frames) || state.frames.length === 0) {
    errors.push(`${where}.frames must be a non-empty array`);
  } else if (!state.frames.every(isNonNegativeInt)) {
    errors.push(`${where}.frames must contain only non-negative integers`);
  }
  if (!(typeof state.fps === 'number' && state.fps > 0)) {
    errors.push(`${where}.fps must be a positive number`);
  }
  if (typeof state.loop !== 'boolean') {
    errors.push(`${where}.loop must be a boolean`);
  }
  if (state.holdLastFrame !== undefined && typeof state.holdLastFrame !== 'boolean') {
    errors.push(`${where}.holdLastFrame must be a boolean when present`);
  }
  if (state.events !== undefined) {
    if (!Array.isArray(state.events)) {
      errors.push(`${where}.events must be an array when present`);
    } else {
      const frames = Array.isArray(state.frames) ? (state.frames as number[]) : [];
      state.events.forEach((ev, i) => validateEvent(name, i, frames, ev, errors));
    }
  }
}

/** Validate an arbitrary parsed object against the v1 animation schema. Never throws. */
export function validateAnimationMeta(input: unknown): ValidationResult {
  const errors: string[] = [];
  if (!isObject(input)) {
    return { valid: false, errors: ['metadata must be an object'] };
  }
  if (input.version !== ANIM_SCHEMA_VERSION) {
    errors.push(`version must be ${ANIM_SCHEMA_VERSION} (got ${String(input.version)})`);
  }
  if (typeof input.image !== 'string' || input.image.length === 0) {
    errors.push('image must be a non-empty string');
  }
  if (!isPositiveInt(input.frameWidth)) {
    errors.push('frameWidth must be a positive integer');
  }
  if (!isPositiveInt(input.frameHeight)) {
    errors.push('frameHeight must be a positive integer');
  }
  if (input.scale !== undefined && !(typeof input.scale === 'number' && input.scale > 0)) {
    errors.push('scale must be a positive number when present');
  }
  if (input.visualScale !== undefined && !isPositiveNumber(input.visualScale)) {
    errors.push('visualScale must be a positive number when present');
  }
  if (input.visualOffsetX !== undefined && !isFiniteNumber(input.visualOffsetX)) {
    errors.push('visualOffsetX must be a finite number when present');
  }
  if (input.visualOffsetY !== undefined && !isFiniteNumber(input.visualOffsetY)) {
    errors.push('visualOffsetY must be a finite number when present');
  }
  if (input.shadowOffsetY !== undefined && !isFiniteNumber(input.shadowOffsetY)) {
    errors.push('shadowOffsetY must be a finite number when present');
  }
  if (input.shadowScale !== undefined && !isPositiveNumber(input.shadowScale)) {
    errors.push('shadowScale must be a positive number when present');
  }

  const states = input.states;
  const stateNames = isObject(states) ? Object.keys(states) : [];
  if (!isObject(states) || stateNames.length === 0) {
    errors.push('states must be a non-empty object');
  } else {
    for (const name of stateNames) validateState(name, states[name], errors);
  }

  if (typeof input.defaultState !== 'string' || input.defaultState.length === 0) {
    errors.push('defaultState must be a non-empty string');
  } else if (!stateNames.includes(input.defaultState)) {
    errors.push(`defaultState "${input.defaultState}" is not present in states`);
  }

  if (errors.length > 0) return { valid: false, errors };
  return { valid: true, meta: input as unknown as AnimationMeta };
}

/** Throwing variant for load paths that prefer an exception (preview tool, loaders). */
export function assertValidAnimationMeta(input: unknown, source = 'animation metadata'): AnimationMeta {
  const result = validateAnimationMeta(input);
  if (!result.valid) {
    throw new Error(`Invalid ${source}:\n  - ${result.errors.join('\n  - ')}`);
  }
  return result.meta;
}
